import { Button } from "@/components/ui/button";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  useOnboarding,
  useOnboardingClientId,
} from "@/features/onboarding/hooks/use-onboarding";
import { useTranslation } from "@/features/utils/i18n";
import { SparkleIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * "Votre configuration n'est pas terminée — Reprendre", above every back-office
 * page while the owner's wizard is `in_progress`.
 *
 * `useLaunchOnboardingForOwners` only sends back a wizard nobody has started
 * (`pending`): dragging someone who left midway back on every page would be
 * worse. But nothing else told them it was waiting, so a client who stepped
 * out ("Terminer plus tard", or any other page) had a back-office running on
 * the defaults and no idea why. This is the reminder, and the way back.
 */
export function ResumeOnboardingBanner() {
  const { t } = useTranslation("onboarding");
  const clientId = useOnboardingClientId();
  const navigate = useNavigate();
  const isOwner = useHasRole("OWNER");
  // Same query as the launcher: nobody but an owner pays for it.
  const { data } = useOnboarding({ enabled: isOwner });

  if (!isOwner || data?.onboarding.status !== "in_progress") return null;

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center">
      <SparkleIcon
        className="text-primary hidden h-5 w-5 shrink-0 sm:block"
        weight="fill"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t("resume.title")}</p>
        <p className="text-muted-foreground text-sm">
          {t("resume.description")}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        className="shrink-0 self-start sm:self-center"
        onClick={() => navigate(`/${clientId}/onboarding`)}
      >
        {t("resume.action")}
      </Button>
    </div>
  );
}
